import { useEffect } from 'react';
import { createPortal } from 'react-dom';

const modalRoot = () => {
  if (typeof document === 'undefined') return null;

  let root = document.getElementById('modal-root');

  if (!root) {
    root = document.createElement('div');
    root.setAttribute('id', 'modal-root');
    document.body.appendChild(root);
  }

  return root;
};

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  icon,
  bodyClassName,
  /** Tailwind z-index class for overlay (e.g. z-[70] when stacking above another dialog) */
  zIndexClass = 'z-50',
  /** When true, Escape does not call onClose (e.g. a nested dialog is open) */
  disableEscapeClose = false,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !disableEscapeClose) {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, disableEscapeClose]);

  if (!isOpen) {
    return null;
  }

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-7xl',
    '2xl': 'max-w-[70%]',
    '3xl': 'max-w-[95%]',
  };

  const root = modalRoot();

  if (!root) {
    return null;
  }

  return createPortal(
    <div
      className={`fixed inset-0 flex items-center justify-center bg-slate-900/40 px-4 py-8 backdrop-blur-sm ${zIndexClass}`}
    >
      <div
        className={`relative flex max-h-[calc(100vh-4rem)] w-full flex-col ${sizeClasses[size] ?? sizeClasses.md} overflow-hidden rounded-2xl border border-border bg-surface text-text-primary shadow-lg ring-1 ring-slate-900/5`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 shrink-0 rounded-xl p-2 text-primary transition hover:bg-surface-muted hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 focus:ring-offset-surface"
          aria-label="Close dialog"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="flex flex-col max-h-[90vh] overflow-hidden">
          <div className="px-6 py-6 flex-shrink-0">
            <div className="flex items-center gap-3 border-b border-border pb-4">
              {icon ? <span className="text-primary">{icon}</span> : null}
              {title ? (
                <h2 id="modal-title" className="text-lg font-semibold text-primary uppercase tracking-[0.08em]">
                  {title}
                </h2>
              ) : null}
            </div>
          </div>

          <div className={`px-6 pb-6 flex-1 overflow-y-auto text-sm text-text-secondary ${bodyClassName || ''}`}>
            {children}
          </div>
        </div>

        {footer ? (
          <div className="flex items-center justify-end gap-3 border-t border-border bg-surface-muted px-6 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    root
  );
};

export default Modal;
